import { ApolloProvider } from '@apollo/client';
import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';

import { AnalyticsProvider } from '@/analytics';
import { apolloClient } from '@/api/apollo';
import { useInstallAuthLink } from '@/api/auth';
import { useInstallAuthExpiryHandler } from '@/api/auth-expiry';
import { queryClient } from '@/api/query-client';
import { AppErrorFallback, ErrorBoundary } from '@/core/ErrorBoundary';

import { AppToastProvider } from './AppToastProvider';
import { buildAuth0ProviderConfig } from './auth0-config';
import { ControlModeProvider } from './ControlModeProvider';
import { EnvironmentProvider } from './EnvironmentProvider';
import { I18nProvider } from './I18nProvider';
import { persistLocale, resolveInitialLocale } from './resolve-initial-locale';
import { persistNumerals, resolveInitialNumerals } from './resolve-initial-numerals';
import { TermsAcceptanceProvider } from './TermsAcceptanceProvider';
import { TermsGate } from './TermsGate';
import { ThemeProvider } from './ThemeProvider';
import { WorkspaceHydrator } from './WorkspaceHydrator';
import { WorkspaceProvider, type Workspace } from './WorkspaceProvider';

import type { PropsWithChildren, ReactElement } from 'react';

// The tenant-hydration gate is its own module (it is ~135 lines of gate
// reasoning, and `WorkspaceHydrator.test.tsx` already named it). Re-exported
// here because five surfaces import it from this path.
export { WorkspaceHydrator } from './WorkspaceHydrator';

/**
 * Bootstrap-only seed workspace. Phase 2 Pass 2 replaces this with the
 * `me { workspaces }` Apollo query — see the `Open questions` block in
 * `plan/STATUS.md`. Until then the chrome (TopBar WorkspaceSwitcher) has
 * a single "Personal" workspace to render so the shell is non-empty.
 */
const SEED_WORKSPACES: readonly Workspace[] = [
  { id: 'ws-personal', name: 'Personal', slug: 'personal' },
];

/**
 * Mounts inside Auth0Provider so `useAuth0` is available. Installs the
 * Auth0 token getter into the Apollo link chain so every subsequent GraphQL
 * request carries a Bearer token.
 */
function AuthLinkInstaller({ children }: PropsWithChildren): ReactElement {
  useInstallAuthLink();
  // Installs the central 401 → re-login handler the Apollo error link calls.
  useInstallAuthExpiryHandler();
  return <>{children}</>;
}

/**
 * Auth0Provider wired to the router. Must render INSIDE `BrowserRouter` so
 * `useNavigate` is available.
 *
 * `onRedirectCallback` is the critical piece: after Auth0 redirects back with
 * `?code&state`, the SDK exchanges the code and then calls this. We
 * router-navigate to the originally-requested page (carried as
 * `appState.returnTo` by `loginWithRedirect`), `replace`-ing the callback URL
 * so the `?code&state` never lingers and the SPA lands on the dashboard rather
 * than getting stuck on the landing route. Without it the post-login navigation
 * races on first return ("fails on return, works on refresh").
 */
function Auth0ProviderWithNavigate({ children }: PropsWithChildren): ReactElement {
  const navigate = useNavigate();
  const onRedirectCallback = useCallback(
    (appState?: AppState): void => {
      const returnTo = typeof appState?.returnTo === 'string' ? appState.returnTo : '/home';
      void navigate(returnTo, { replace: true });
    },
    [navigate],
  );

  // Token-storage choice (cacheLocation="memory") lives in `buildAuth0ProviderConfig`
  // so it is unit-tested against regression — see auth0-config.ts for the rationale
  // (2026-07 pentest: no Auth0 tokens in localStorage).
  return (
    <Auth0Provider {...buildAuth0ProviderConfig()} onRedirectCallback={onRedirectCallback}>
      {children}
    </Auth0Provider>
  );
}

/**
 * Root application providers.
 *
 * Order rationale (outer to inner):
 *
 *   BrowserRouter      ─ keeps useNavigate / useLocation usable everywhere
 *     Auth0Provider    ─ identity; consumed by AuthGuard + any auth-aware UI
 *       AuthLinkInstaller ─ installs Auth0 token getter into Apollo link chain
 *         ApolloProvider ─ GraphQL client; reads Auth0 token in its link
 *           I18nProvider ─ locale + html dir + useT(); inside Apollo so a
 *                          future locale-changes-by-graphql-mutation flow
 *                          can be wired without re-nesting
 *             ThemeProvider     ─ visual mode; persisted in localStorage
 *               ControlModeProvider ─ Simple / Advanced control depth; the same
 *                                     shape of persisted reader preference as the
 *                                     theme, and read by the same top-bar menu
 *                 WorkspaceProvider     ─ current tenant scope
 *                   EnvironmentProvider ─ Dev / Prod toggle on top of the workspace
 *                     QueryClientProvider ─ non-GraphQL state (small footprint)
 *                       AnalyticsProvider ─ PostHog wiring; innermost so it reads
 *                                           router + auth + locale + workspace
 *
 * Keep this file thin — provider implementation lives in the individual
 * modules (apollo.ts, ThemeProvider.tsx, …).
 */
export function Providers({ children }: PropsWithChildren): ReactElement {
  // Resolve once on mount: saved preference → navigator.language → EN.
  // Catalog-aware mode (no `messages` prop) resolves strings per locale via
  // the shared `getCatalog`; `persistLocale` records the choice across reloads.
  const initialLocale = useMemo(() => resolveInitialLocale(), []);
  // Arabic-numeral preference (ADR-0141): Western default, Eastern opt-in.
  const initialNumerals = useMemo(() => resolveInitialNumerals(), []);

  return (
    <BrowserRouter>
      <Auth0ProviderWithNavigate>
        <AuthLinkInstaller>
          <ApolloProvider client={apolloClient}>
            <I18nProvider
              initialLocale={initialLocale}
              onLocaleChange={persistLocale}
              initialNumerals={initialNumerals}
              onNumeralsChange={persistNumerals}
            >
              <ThemeProvider>
                {/* Reader preference, not a permission — it decides how much
                    control a supporting surface offers, and it is switched from
                    the top bar's user menu, so it has to sit above the router. */}
                <ControlModeProvider>
                  {/* App-root toast region (A18). Outside the ErrorBoundary so
                    transient feedback (and the Apollo network-failure toast)
                    survives a page render failure. */}
                  <AppToastProvider>
                    {/* Global render-error boundary. Inside I18nProvider +
                      ThemeProvider so its fallback is localized + themed; wraps
                      the whole app body so one bad page render can't white-screen
                      the dashboard. */}
                    <ErrorBoundary fallback={(reset) => <AppErrorFallback onRetry={reset} />}>
                      {/* ADR-0497 — the acceptance answer is fetched HERE (above the
                        workspace hydrator, so its query flies in the same tick as
                        MyTenants rather than after it) and ENFORCED below (so the
                        two waits overlap instead of serialising). The provider
                        never withholds children; TermsGate does the blocking. */}
                      <TermsAcceptanceProvider>
                        <WorkspaceProvider initialWorkspaces={SEED_WORKSPACES}>
                          <WorkspaceHydrator>
                            <TermsGate>
                              <EnvironmentProvider>
                                <QueryClientProvider client={queryClient}>
                                  <AnalyticsProvider>{children}</AnalyticsProvider>
                                </QueryClientProvider>
                              </EnvironmentProvider>
                            </TermsGate>
                          </WorkspaceHydrator>
                        </WorkspaceProvider>
                      </TermsAcceptanceProvider>
                    </ErrorBoundary>
                  </AppToastProvider>
                </ControlModeProvider>
              </ThemeProvider>
            </I18nProvider>
          </ApolloProvider>
        </AuthLinkInstaller>
      </Auth0ProviderWithNavigate>
    </BrowserRouter>
  );
}
